import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  taskMemberChartHeight,
  taskMemberMetricsChartConfig,
  type TaskMemberMetricsRow,
} from "@/components/tasks/task-member-metrics-chart-config";

interface TaskMemberMetricsBarChartProps {
  data: TaskMemberMetricsRow[];
  title?: string;
  loading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  showBorder?: boolean;
}

export function TaskMemberMetricsBarChart({
  data,
  title,
  loading,
  loadingMessage = "Carregando gráfico...",
  emptyMessage = "Sem dados para o período selecionado",
  showBorder = false,
}: TaskMemberMetricsBarChartProps) {
  const chartHeight = taskMemberChartHeight(data.length);

  if (loading) {
    return (
      <div
        className={`h-[280px] flex items-center justify-center rounded-lg ${showBorder ? "border bg-muted/10" : ""}`}
      >
        <p className="text-sm text-muted-foreground">{loadingMessage}</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div
        className={`h-[280px] flex items-center justify-center rounded-lg ${showBorder ? "border bg-muted/10" : ""}`}
      >
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  const longestName = data.reduce((max, row) => Math.max(max, row.name.length), 0);
  const yAxisWidth = Math.min(160, Math.max(88, longestName * 7));

  const chart = (
    <ChartContainer
      config={taskMemberMetricsChartConfig}
      className="w-full aspect-auto"
      style={{ height: chartHeight }}
    >
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 8, right: 24, left: 4, bottom: 4 }}
        barCategoryGap="20%"
        barGap={4}
      >
        <CartesianGrid
          horizontal={false}
          strokeDasharray="3 3"
          className="stroke-border/60"
        />
        <XAxis
          type="number"
          allowDecimals={false}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={yAxisWidth}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
        />
        <ChartTooltip
          cursor={{ fill: "hsl(var(--muted))", opacity: 0.25 }}
          content={
            <ChartTooltipContent
              className="border bg-popover text-popover-foreground shadow-md"
              labelFormatter={(label) => String(label)}
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        <Bar
          dataKey="completed"
          name="Concluídas"
          fill="var(--color-completed)"
          radius={[0, 4, 4, 0]}
          barSize={10}
        />
        <Bar
          dataKey="positive"
          name="Positivas"
          fill="var(--color-positive)"
          radius={[0, 4, 4, 0]}
          barSize={10}
        />
        <Bar
          dataKey="negative"
          name="Negativas"
          fill="var(--color-negative)"
          radius={[0, 4, 4, 0]}
          barSize={10}
        />
        <Bar
          dataKey="overdue"
          name="Atrasadas"
          fill="var(--color-overdue)"
          radius={[0, 4, 4, 0]}
          barSize={10}
        />
      </BarChart>
    </ChartContainer>
  );

  if (!title && !showBorder) {
    return chart;
  }

  return (
    <div className={`rounded-lg space-y-2 ${showBorder ? "border p-3 sm:p-4" : ""}`}>
      {title ? <h4 className="text-sm font-medium">{title}</h4> : null}
      {chart}
    </div>
  );
}
